import { isLightColor } from "../lib/geometry";
import type { Box, Connector, FleetGeometry } from "../lib/geometry";
import { isHighlighted } from "../lib/selection";
import type { HighlightTarget, Selection } from "../lib/selection";
import type { TopologyPayload } from "../lib/types/topology";
import type { ViewMode } from "../lib/viewMode";


interface TopTierRailsProps {
  geometry: FleetGeometry;
  topology: TopologyPayload;
  selection?: Selection;
  onSelect?: (s: Selection | undefined) => void;
  /** Double-click = select AND jump the LOPA to the highlighted seats. */
  onActivate?: (s: Selection) => void;
  viewMode: ViewMode;
}


/**
 * "No hanging lines" rule: a connector may only stay highlighted if BOTH of
 * its endpoint boxes are highlighted. Connectors anchor to box top/bottom
 * centres, so endpoints resolve to boxes positionally — one mechanism covers
 * every fleet and tier (CS→NC, FS→uplink→ADB, port stubs, MCU→port).
 */
export function makeEndpointCheck(
  geometry: FleetGeometry,
  selection: Selection | undefined,
  topology: TopologyPayload,
  viewMode: ViewMode,
): (c: Connector) => boolean {
  if (!selection) return () => true;
  // Availability view draws the same head-end tree as Data view.
  const view = viewMode === "mcu" ? "mcu" : "data";
  const anchor = new Map<string, Box>();
  const visBoxes: Box[] = [];
  for (const b of [...geometry.topTierBoxes, ...geometry.portBoxes]) {
    // Only boxes visible in THIS view may claim anchors — a Data chip and
    // an MCU power chip can occupy the same spot under a parent (ex-GOL's
    // P13 vs P3), and the wrong one silently killed the cable highlight.
    if (b.view && b.view !== "both" && b.view !== view) continue;
    visBoxes.push(b);
    const cx = Math.round(b.x + b.width / 2);
    const cy = Math.round(b.y + b.height / 2);
    anchor.set(`${cx},${Math.round(b.y)}`, b);
    anchor.set(`${cx},${Math.round(b.y + b.height)}`, b);
    // Side-edge midpoints anchor same-row (edge-to-edge) links.
    anchor.set(`${Math.round(b.x)},${cy}`, b);
    anchor.set(`${Math.round(b.x + b.width)},${cy}`, b);
  }
  // Cables that leave/enter OFF-CENTRE (the eX3 CS→NC cross mesh runs on
  // separated lanes) miss the centre anchors — resolve those endpoints by
  // perimeter containment instead, or they count as free ends and hang
  // bright off a dimmed box.
  const TOL = 2;
  const edgeBox = (x: number, y: number): Box | undefined =>
    visBoxes.find((b) => {
      const withinX = x >= b.x - TOL && x <= b.x + b.width + TOL;
      const withinY = y >= b.y - TOL && y <= b.y + b.height + TOL;
      const onTopBottom =
        Math.abs(y - b.y) <= TOL || Math.abs(y - (b.y + b.height)) <= TOL;
      const onSides =
        Math.abs(x - b.x) <= TOL || Math.abs(x - (b.x + b.width)) <= TOL;
      return (onTopBottom && withinX) || (onSides && withinY);
    });
  const ok = (x: number, y: number): boolean => {
    const b =
      anchor.get(`${Math.round(x)},${Math.round(y)}`) ?? edgeBox(x, y);
    if (!b) return true;  // free end (e.g. a rail into the seat grid)
    return isHighlighted(selection, boxToTarget(b), topology);
  };
  return (c) => ok(c.x1, c.y1) && ok(c.x2, c.y2);
}


export default function TopTierRails({
  geometry, topology, selection, onSelect, onActivate, viewMode,
}: TopTierRailsProps) {
  // Availability view keeps the Data-view head-end tree; only seat colours
  // change (SeatGrid recolours by trip score).
  const view = viewMode === "mcu" ? "mcu" : "data";
  // eX1 boxes are FS/AI (data tier) + MCU (power tier). MCU view keeps AI
  // visible — per Edwin's spec, AI is the bridge between MCU and the data
  // side; FS is hidden in MCU view. eX3 has no MCU boxes, so the filter is
  // a no-op there and the MCU button is disabled upstream in ConfigToolbar.
  // Socket chips / entry tabs carry an explicit view tag.
  const boxes = geometry.topTierBoxes.filter((b) => {
    if (b.view === "both") return true;
    if (b.view) return b.view === view;
    if (view === "mcu") return b.kind === "mcu" || b.kind === "ai";
    return b.kind !== "mcu";  // dport boxes are built for Data view only
  });
  // FS/AI → MCU hop connectors aren't authoritative in the DB yet (Edwin
  // will populate them later); hide them in both views to avoid implying
  // connections that don't exist.
  const topConnectors = geometry.connectors.filter((c) => {
    if (c.conn === "port")  return false;
    if (c.conn === "hop")   return false;
    if (c.conn === "power") return view === "mcu";  // AI→MCU uplinks
    return view === "data";  // sub / link (iLOPA_Flag) / chain / cs
  });
  const hasSelection = Boolean(selection);
  const endpointsBright = makeEndpointCheck(geometry, selection, topology, view);

  return (
    <g>
      {topConnectors.map((c, i) => {
        const bright =
          isHighlighted(
            selection,
            { kind: "connector", conn: c.conn, nc: c.nc, port: c.port },
            topology,
          ) && endpointsBright(c);
        return (
          <ConnectorLine
            key={`top-conn-${i}`}
            c={c}
            opacity={bright ? (c.opacity ?? 1) : 0.06}
            emphasised={hasSelection && bright}
          />
        );
      })}
      {boxes.map((b, i) => {
        const target = boxToTarget(b);
        const bright = isHighlighted(selection, target, topology);
        return (
          <BoxNode
            key={`top-${i}`}
            b={b}
            opacity={bright ? 1 : 0.15}
            emphasised={hasSelection && bright}
            onClick={onSelect && b.kind !== "leaf"
              ? () => onSelect(boxToSelection(b))
              : undefined}
            onDoubleClick={onActivate && b.kind !== "leaf"
              ? () => onActivate(boxToSelection(b))
              : undefined}
          />
        );
      })}
      {/* v2 port annotations (Server_Port / LRU_Port from lru_lookup_ilopa),
          drawn as small pills so they stay legible over cables. Muted while
          a selection is active so the highlighted chain reads. */}
      {(geometry.labels ?? [])
        .filter((l) => l.view === view)
        .map((l, i) => {
          const w = l.text.length * 5.2 + 10;
          const cx =
            l.anchor === "middle" ? l.x :
            l.anchor === "end"    ? l.x - w / 2 :
            l.x + w / 2;
          // Midpoint cable badges: fully-rounded pills sitting ON the cable
          // (opaque fill breaks the dashes behind them). Redundant cables
          // get the cream/amber treatment; plain port hints stay white.
          const redundant = l.tone === "redundant";
          return (
            <g
              key={`lbl-${i}`}
              opacity={hasSelection ? 0.3 : 1}
              pointerEvents="none"
              style={{ transition: "opacity 180ms ease" }}
            >
              <rect
                x={cx - w / 2} y={l.y - 11} width={w} height={16}
                rx={8}
                fill={redundant ? "#FEF3C7" : "#FFFFFF"}
                stroke={redundant ? "#F59E0B" : "#CBD5E1"}
                strokeWidth={redundant ? 1.2 : 0.8}
              />
              <text
                x={cx} y={l.y}
                fontSize={8} fontWeight={redundant ? 700 : 600}
                fill={redundant ? "#B45309" : "#475569"}
                textAnchor="middle"
              >
                {l.text}
              </text>
            </g>
          );
        })}
    </g>
  );
}


interface ConnectorLineProps {
  c: Connector;
  opacity?: number;
  /** When true, doubles stroke-width to emphasise the chain. */
  emphasised?: boolean;
}

// One highlight style for every connector, on every fleet: darker slate,
// heavier stroke, near-full opacity. Baseline + dimmed states come from the
// shared CONN_* constants via each connector's own fields. iLOPA_Flag
// secondary edges render dashed in amber (redundancy), with a marching-ants
// animation while their chain is highlighted.
export function ConnectorLine({ c, opacity, emphasised }: ConnectorLineProps) {
  const baseStroke = c.dashed ? "#D97706" : c.stroke;
  const common = {
    stroke: emphasised ? (c.dashed ? "#B45309" : "#475569") : baseStroke,
    strokeWidth: emphasised ? 2.2 : c.width,
    opacity: emphasised ? 0.95 : (opacity ?? c.opacity ?? 1),
    strokeDasharray: c.dashed ? "5 4" : undefined,
    strokeLinecap: "round" as const,
    className: emphasised && c.dashed ? "lopa-ants" : undefined,
    style: { transition: "stroke 180ms ease, stroke-width 180ms ease, opacity 180ms ease" },
  };
  if (c.path) {
    return <path d={c.path} fill="none" {...common} />;
  }
  return <line x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2} {...common} />;
}


interface BoxNodeProps {
  b: Box;
  opacity?: number;
  /** When true, applies the heavier near-black outline for the active chain. */
  emphasised?: boolean;
  onClick?: () => void;
  /** Double-click = select AND jump the LOPA to the highlighted seats. */
  onDoubleClick?: () => void;
}

export function BoxNode({
  b, opacity, emphasised, onClick, onDoubleClick,
}: BoxNodeProps) {
  // Ports (in and out alike) straddle their card's border — small dark
  // sockets in one uniform style; only chain-coloured ones keep their fill.
  const isSmallPort = b.kind === "port" || b.kind === "dport" ||
    b.kind === "uport" || b.kind === "aiport" || b.kind === "inport";
  const isCard = !isSmallPort;
  // No sub-label → centre the title vertically (no empty band below it).
  // h ≥ 56 cards carry in-port sockets on their top edge — the text block
  // sits lower so the straddling chips never touch the title.
  const titleY = !b.secondaryLabel
    ? b.y + b.height / 2 + (isSmallPort ? 3.5 : 4)
    : b.y + (b.height >= 56 ? 26 : b.height >= 50 ? 21 : 19);
  const subY   = b.kind === "dport" || b.kind === "uport"
    ? b.y + 24
    : b.y + (b.height >= 56 ? 40 : b.height >= 50 ? 37 : 33);
  const titleFs = isSmallPort ? 9.5 : (b.kind === "mcu" ? 14 : 13);
  // Chain-coloured port boxes can land on light palette colours (amber/lime)
  // — flip to dark text there for contrast.
  const lightFill = isSmallPort && isLightColor(b.fill);
  const titleColor =
    b.kind === "mcu" ? "#222" :
    lightFill ? "#1E293B" : "white";
  const subColor   =
    b.kind === "mcu" ? "#444" :
    lightFill ? "#334155" :
    (b.kind === "cs" || b.kind === "fs" || b.kind === "ai") ? "#E8F0F9" :
    "#fff";
  // Micro-sockets get a heavier light rim — the bezel is what sells the
  // "seated into the card edge" join.
  const baseStrokeW = b.kind === "mcu" ? 1.5 : isSmallPort ? 1.4 : 1;
  const strokeW = emphasised ? 2.4 : baseStrokeW;
  const strokeColor = emphasised ? "#111" : b.stroke;

  return (
    <g
      id={boxId(b)}
      opacity={opacity ?? 1}
      className={onClick ? "lopa-node cursor-pointer" : undefined}
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      onDoubleClick={onDoubleClick
        ? (e) => { e.stopPropagation(); onDoubleClick(); }
        : undefined}
      style={{ transition: "opacity 180ms ease" }}
    >
      <rect
        x={b.x} y={b.y} width={b.width} height={b.height}
        fill={b.fill} stroke={strokeColor}
        strokeWidth={strokeW}
        rx={isSmallPort ? 4 : 7}
        filter={isCard ? "url(#lopaCardShadow)" : undefined}
        style={{ transition: "stroke 180ms ease, stroke-width 180ms ease" }}
      />
      <text
        x={b.x + b.width / 2}
        y={titleY}
        textAnchor="middle"
        fontSize={titleFs}
        fontWeight={700}
        fill={titleColor}
        pointerEvents="none"
      >
        {b.primaryLabel}
      </text>
      {b.secondaryLabel && (
        <text
          x={b.x + b.width / 2}
          y={subY}
          textAnchor="middle"
          fontSize={isSmallPort ? 7 : 9}
          fill={subColor}
          opacity={isSmallPort ? 0.92 : 1}
          pointerEvents="none"
        >
          {b.secondaryLabel}
        </text>
      )}
    </g>
  );
}


export function boxToSelection(b: Box): Selection {
  switch (b.kind) {
    // Entry tab: selection semantics delegate to the host box.
    case "inport":
      return boxToSelection({ ...b, kind: b.hostKind ?? "fs" });
    // Out-port socket: selects that specific port's downstream tree.
    case "aiport":
      return {
        kind: "aiport",
        unit: b.parentKind ?? "AI",
        num: b.ref.primary,
        port: b.ref.secondary ?? 0,
      };
    case "cs":  return { kind: "cs",  id: b.ref.primary };
    case "nc":  return { kind: "nc",  id: b.ref.primary };
    case "mcu": return { kind: "nc",  id: b.ref.primary };
    case "fs":  return { kind: "fs",  idx: b.ref.primary - 1 };
    case "ai":  return { kind: "ai",  idx: b.ref.primary - 1 };
    // Leaf branches aren't clickable; map to the parent AI defensively.
    case "leaf": return { kind: "ai", idx: b.ref.primary - 1 };
    case "adb": return { kind: "adb", num: b.ref.primary };
    // FS-uplink port: 1:1 with its downstream ADB — selecting it selects the ADB.
    case "uport": return { kind: "adb", num: b.ref.primary };
    case "dport":
      return { kind: "dport", num: b.ref.primary, port: b.ref.secondary! };
    case "port":
      return { kind: "port", nc: b.ref.primary, port: b.ref.secondary! };
  }
}


export function boxToTarget(b: Box): HighlightTarget {
  switch (b.kind) {
    // Entry tab: highlights with the host box.
    case "inport":
      return boxToTarget({ ...b, kind: b.hostKind ?? "fs" });
    case "cs":  return { kind: "cs"  as const, id:  b.ref.primary };
    case "nc":  return { kind: "nc"  as const, id:  b.ref.primary };
    case "mcu": return { kind: "nc"  as const, id:  b.ref.primary };
    case "fs":  return { kind: "fs"  as const, idx: b.ref.primary - 1 };
    case "ai":  return { kind: "ai"  as const, idx: b.ref.primary - 1 };
    // Leaves carry their own parent triple — they only light with that
    // exact parent unit / port (not with sibling servers).
    case "leaf": return {
      kind: "leaf" as const,
      unit: b.parentKind ?? "AI",
      num:  b.ref.primary,
      port: b.ref.secondary ?? null,
    };
    // Out-port sockets highlight per (unit, num, port) so each port's
    // downstream tree traces independently.
    case "aiport": return {
      kind: "aiport" as const,
      unit: b.parentKind ?? "AI",
      num:  b.ref.primary,
      port: b.ref.secondary ?? 0,
    };
    case "adb": return { kind: "adb" as const, num: b.ref.primary };
    case "uport": return { kind: "adb" as const, num: b.ref.primary };
    case "dport":
      return { kind: "dport" as const, num: b.ref.primary, port: b.ref.secondary! };
    case "port":
      return { kind: "port" as const, nc: b.ref.primary, port: b.ref.secondary! };
  }
}


/** Stable DOM id so FleetSVG can scrollIntoView() the selected box. */
function boxId(b: Box): string {
  switch (b.kind) {
    case "inport":
      return `inport-${b.hostKind}-${b.ref.primary}-${b.ref.secondary ?? 0}-${Math.round(b.x)}`;
    case "cs":   return `cs-${b.ref.primary}`;
    case "nc":   return `nc-${b.ref.primary}`;
    case "mcu":  return `nc-${b.ref.primary}`;
    case "fs":   return `fs-${b.ref.primary - 1}`;
    case "ai":   return `ai-${b.ref.primary - 1}`;
    case "leaf": return `leaf-${b.ref.primary}`;
    case "aiport": return `aiport-${b.ref.primary}-${b.ref.secondary}`;
    case "adb":  return `adb-${b.ref.primary}`;
    case "uport": return `uport-${b.ref.primary}-${b.ref.secondary}`;
    case "dport": return `dport-${b.ref.primary}-${b.ref.secondary}`;
    case "port": return `port-${b.ref.primary}-${b.ref.secondary}`;
  }
}
